import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ShieldCheck, UserPlus, X, Check, Link2, Unlink, Clock } from "lucide-react";
import { toast } from "sonner";

export default function ParentalControlCard() {
  const [state, setState] = useState(null);
  const [approvals, setApprovals] = useState([]);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/parental/status");
      setState(data);
      if (data?.is_parent) {
        try {
          const res = await api.get("/parental/approvals");
          setApprovals(res.data?.items || []);
        } catch { /* silent */ }
      }
    } catch { /* silent */ }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const decideApproval = async (id, action) => {
    setApprovals((prev) => prev.map((a) => (a.id === id ? { ...a, status: action === "approve" ? "approved" : "declined" } : a)));
    try {
      await api.post(`/parental/approvals/${id}/${action}`);
      toast.success(action === "approve" ? "Conversation approved" : "Kept on hold");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); refresh(); }
  };

  if (!state) return null;
  if (!state.is_child && !state.is_parent) return null;

  const invite = async () => {
    if (!email.trim()) { toast.error("Enter an email"); return; }
    setBusy(true);
    try {
      await api.post("/parental/invite", { email: email.trim().toLowerCase() });
      toast.success("Invite sent");
      setEmail("");
      refresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const accept = async (id) => {
    setBusy(true);
    try {
      await api.post(`/parental/${id}/accept`);
      toast.success("Link accepted");
      refresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const decline = async (id) => {
    if (!window.confirm("Decline this parental link invite?")) return;
    try { await api.post(`/parental/${id}/decline`); refresh(); toast.success("Declined"); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const unlink = async () => {
    if (!window.confirm("Remove parental link? Messaging will be locked again until relinked.")) return;
    try { await api.delete("/parental/unlink"); toast.success("Unlinked"); refresh(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const roleLabel = state.is_child ? "Parent" : "Child athlete";

  return (
    <div className="bg-white border border-[#E6E6E6] rounded-xl p-5" data-testid="parental-card">
      <div className="flex items-center gap-2 mb-3">
        <ShieldCheck size={16} className="text-[#074572]" />
        <h3 className="text-sm font-semibold text-zinc-900" style={{ fontFamily: 'Outfit' }}>Parental Control</h3>
        {state.link && (
          <Badge className="text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-200 ml-auto">
            <Check size={9} className="mr-0.5" /> Linked
          </Badge>
        )}
      </div>

      {state.is_parent && approvals.length > 0 && (
        <div className="mb-3 space-y-2" data-testid="parental-approvals">
          <p className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wide">Conversation approvals</p>
          {approvals.map((a) => {
            const pending = a.status === "pending";
            const declined = a.status === "declined";
            return (
              <div key={a.id} className={`p-3 rounded-md border ${pending ? "bg-[#F85037]/5 border-[#F85037]/30" : "bg-zinc-50 border-[#E6E6E6]"}`} data-testid="approval-item">
                <p className="text-xs text-zinc-800 mb-1">
                  <span className="font-semibold">{a.child_name}</span>
                  {a.initiated_by === "child" ? " wants to message " : " received a message from "}
                  <span className="font-semibold">{a.other_user_name}</span>
                </p>
                {a.preview && <p className="text-[11px] text-zinc-500 italic mb-2">“{a.preview}”</p>}
                {pending || declined ? (
                  <div className="flex gap-2">
                    {pending ? (
                      <Button size="sm" variant="outline" onClick={() => decideApproval(a.id, "decline")} className="flex-1 h-8 text-xs" data-testid={`approval-decline-${a.id}`}>
                        <X size={12} className="mr-1" /> Decline
                      </Button>
                    ) : (
                      <span className="flex-1 flex items-center justify-center h-8 text-[11px] text-zinc-500 bg-white rounded border border-[#E6E6E6]">On hold</span>
                    )}
                    <Button size="sm" onClick={() => decideApproval(a.id, "approve")} className="flex-1 h-8 bg-[#074572] hover:bg-[#053a60] text-white text-xs" data-testid={`approval-approve-${a.id}`}>
                      <Check size={12} className="mr-1" /> Approve
                    </Button>
                  </div>
                ) : (
                  <Badge className="text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-200">
                    <Check size={9} className="mr-0.5" /> Approved
                  </Badge>
                )}
              </div>
            );
          })}
        </div>
      )}

      {state.link ? (
        <div className="space-y-3">
          <div className="flex items-center gap-3 p-3 bg-zinc-50 rounded-md border border-[#E6E6E6]" data-testid="parental-linked-other">
            <div className="w-9 h-9 rounded-full bg-[#074572] text-white flex items-center justify-center text-xs font-semibold">
              {(state.other?.name || "?").split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2)}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-zinc-900 truncate">{state.other?.name}</p>
              <p className="text-[11px] text-zinc-500 truncate">{roleLabel} · {state.other?.email}</p>
            </div>
          </div>
          <Button variant="outline" onClick={unlink} className="w-full border-[#F85037]/30 text-[#F85037] hover:bg-[#F85037]/5 h-9 text-xs" data-testid="parental-unlink-btn">
            <Unlink size={12} className="mr-1.5" /> Unlink {roleLabel}
          </Button>
        </div>
      ) : (
        <>
          {state.pending?.length > 0 && (
            <div className="space-y-2 mb-3">
              {state.pending.map(p => (
                <div key={p.id} className="p-3 bg-amber-50 border border-amber-200 rounded-md" data-testid={`parental-pending-${p.id}`}>
                  <div className="flex items-center gap-2 text-xs text-amber-800 mb-2">
                    <Clock size={12} /> Pending invite
                    {p.admin_initiated && <Badge className="text-[9px] bg-white border-amber-200 text-amber-700">Admin</Badge>}
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => accept(p.id)} disabled={busy || p.initiator_id === (state.is_child ? p.child_id : p.parent_id)} className="flex-1 h-8 bg-[#074572] hover:bg-[#053a60] text-white text-xs" data-testid={`parental-accept-${p.id}`}>
                      <Check size={12} className="mr-1" /> Accept
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => decline(p.id)} className="flex-1 h-8 text-xs" data-testid={`parental-decline-${p.id}`}>
                      <X size={12} className="mr-1" /> Decline
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
          <p className="text-xs text-zinc-500 mb-3">
            {state.is_child
              ? "Link your parent's account to unlock messaging. Your parent will see a read-only copy of every conversation."
              : "Link your child's account to give them messaging access. You'll see a read-only copy of every conversation."}
          </p>
          <div className="flex gap-2">
            <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder={`${roleLabel} email`} className="border-[#E6E6E6] h-9 text-sm" data-testid="parental-invite-email" />
            <Button onClick={invite} disabled={busy} className="bg-[#F85037] hover:bg-[#e04530] text-white h-9 text-xs" data-testid="parental-invite-btn">
              <UserPlus size={12} className="mr-1" /> Invite
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
